/**
 * `GuidancePlayer` — ONE player for the whole visit (SPEC_CAPTURE_UX_R2 §3, SPEC_AUDIO_GUIDANCE §3).
 *
 * The rules it enforces, each of them a line in the spec:
 *   · ONE `HTMLAudioElement`, created inside the unlock gesture ("turn on the camera") and reused for every cue —
 *     never a second element, never `autoplay` (iOS Safari / Chrome autoplay policy);
 *   · a STEP CHANGE cancels any in-flight clip before the next one starts (§3.1 "does not autoplay over itself");
 *   · it NEVER plays while a clip is recording (§3.2, the one rule that protects the evidence: the phone's own
 *     speaker would print the prompt into the evidence audio track and pollute a SPEAK_NONCE challenge). The
 *     interlock lives HERE, not in a screen, because a screen can forget;
 *   · it DUCKS rather than fights: an outside interruption (a phone call, another app taking audio focus) arrives
 *     as a `pause` we did not ask for — the cue stops and does NOT auto-resume; the replay control stays;
 *   · it speaks the EXACT text the screen shows. The cue carries the text; nothing composes a second script;
 *   · MUTED is a state, not a silence: `source` reports which rung was reached (`SERVER_ASSET` · `DEVICE_TTS` ·
 *     `NONE` · `MUTED`) so the surface can show the honest note instead of a dead button.
 *
 * Framework-free on purpose: the decision logic is unit-testable without a DOM, and the React layer
 * (`guidanceStore.ts`) only subscribes.
 */

import { type GuidanceAudioRef, type AssetResult, fetchGuidanceAsset } from './audioAsset';
import { type SpeakHandle, bcp47For, primeSpeech, speak } from './speech';

export type GuidanceSource = 'SERVER_ASSET' | 'DEVICE_TTS' | 'NONE' | 'MUTED';

export interface GuidanceCue {
  /** Identity of the cue — a step change is a key change. `STEP:<shotId>` · `ERROR:camera` · `NOTICE:<n>`. */
  readonly key: string;
  /** The EXACT words on screen. One source of copy for text, captions and speech. */
  readonly text: string;
  /** The deck locale in force (`en` | `hi`) — turned into a BCP-47 tag for the voice. */
  readonly locale: string;
  /**
   * The locale `text` is actually WRITTEN in, when it can differ from `locale` (server-authored step text that has
   * no rendering in the chosen language falls back to English — `shotText`). Absent ⇒ same as `locale` (deck text).
   * A mismatch never reaches the device voice: a Hindi voice reading English is not Hindi guidance (FSD-18 D6).
   */
  readonly textLocale?: string;
  /** Rung 1, when the be sends one (it does not yet). */
  readonly asset?: GuidanceAudioRef | null;
  /**
   * A notice cue: an accessibility AID, never the consent record and never a consent action. The reviewed asset reads
   * it when one exists; otherwise the device voice may (US-03482 G7, decided 2026-11-03 — "Listen to this notice"
   * must speak on every phone, iPhone included). The tap on the consent control stays the only consent.
   */
  readonly aidOnly?: boolean;
}

export interface GuidanceState {
  readonly unlocked: boolean;
  readonly muted: boolean;
  readonly playing: boolean;
  readonly recording: boolean;
  /** The rung REACHED for the last cue attempted, or null before the first attempt. */
  readonly source: GuidanceSource | null;
  readonly cueKey: string | null;
  /** The device voice used on rung 2 — reported, never claimed as a reviewed rendering. */
  readonly voiceName: string | null;
  /** True when an outside interruption stopped the cue (no auto-resume; replay is the way back). */
  readonly ducked: boolean;
}

export interface PlayerDeps {
  /** Made once, inside the unlock gesture. */
  readonly makeAudio?: () => HTMLAudioElement;
  readonly loadAsset?: (ref: GuidanceAudioRef, locale: string) => Promise<AssetResult>;
  /** `onEnd(error?)`: the read finished, or the platform's error code (`not-allowed`, `synthesis-failed`, …). */
  readonly speakText?: (text: string, tag: string, onEnd: (error?: string) => void) => SpeakHandle | null;
  /** Called inside the unlock gesture so iOS WebKit lets later speech start outside one (see `speech.ts`). */
  readonly primeSpeech?: () => void;
}

function baseLocale(locale: string): string {
  return (locale || 'en').toLowerCase().split('-')[0] ?? 'en';
}

/** FSD-18 D6: the device voice speaks only text written in the voice's own language. */
export function textMatchesVoice(cue: Pick<GuidanceCue, 'locale' | 'textLocale'>): boolean {
  return cue.textLocale === undefined || baseLocale(cue.textLocale) === baseLocale(cue.locale);
}

const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

export class GuidancePlayer {
  private el: HTMLAudioElement | null = null;
  private speaking: SpeakHandle | null = null;
  private revokeCurrent: (() => void) | null = null;
  /** Bumped on every play/cancel so a resolution that lost the race cannot start a stale clip. */
  private generation = 0;
  private listeners = new Set<() => void>();
  private state: GuidanceState = {
    unlocked: false,
    muted: false,
    playing: false,
    recording: false,
    source: null,
    cueKey: null,
    voiceName: null,
    ducked: false,
  };
  /** Set while WE pause/stop the element, so our own `pause` event is not read as an interruption. */
  private selfStopping = false;
  /** Identity of the running section-by-section read (§3.2); a new one (or any `cancel()`) abandons the old. */
  private sequenceToken: object = {};

  constructor(private readonly deps: PlayerDeps = {}) {}

  snapshot(): GuidanceState {
    return this.state;
  }

  subscribe(l: () => void): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  private set(patch: Partial<GuidanceState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /**
   * Called INSIDE a user gesture (the "turn on the camera" tap, the replay button, "Listen to this notice").
   * Creates the one element and pokes it with a silent clip so later `src` swaps are permitted on iOS.
   */
  unlock(): void {
    if (!this.el) {
      const make = this.deps.makeAudio ?? (() => new Audio());
      try {
        const el = make();
        el.preload = 'auto';
        el.addEventListener('ended', () => {
          if (this.state.playing) this.set({ playing: false });
        });
        el.addEventListener('pause', () => {
          // Ours ⇒ ignore. Theirs (a call, another app) ⇒ duck: stop, do not auto-resume.
          if (this.selfStopping || !this.state.playing) return;
          this.set({ playing: false, ducked: true });
        });
        this.el = el;
        try {
          el.src = SILENT_WAV;
          void el.play?.()?.catch?.(() => undefined);
        } catch {
          /* a platform that refuses the priming clip still takes the later swap */
        }
      } catch {
        this.el = null;
      }
    }
    // iOS WebKit (Safari and Chrome on iPhone): the first utterance must start inside a gesture. Priming here makes
    // the auto-played step cue and the chained notice sections audible too. Idempotent.
    try {
      (this.deps.primeSpeech ?? (() => primeSpeech()))();
    } catch {
      /* a platform without speech still gets the asset rung and the honest note */
    }
    if (!this.state.unlocked) this.set({ unlocked: true });
  }

  setMuted(muted: boolean): void {
    if (muted) this.cancel();
    this.set({ muted, ...(muted ? { source: 'MUTED' as GuidanceSource } : {}) });
  }

  /** The evidence interlock: true from `start-recording` to the decision. Stops any cue instantly. */
  setRecording(recording: boolean): void {
    if (recording) this.cancel();
    if (this.state.recording !== recording) this.set({ recording });
  }

  /** Stop whatever is speaking/playing AND abandon any section-by-section read. Leaves `muted`/`unlocked` alone. */
  cancel(): void {
    this.sequenceToken = {};
    this.stopMedia();
  }

  /** Stop the media only — `play()` uses this so it does not abandon the sequence that called it. */
  private stopMedia(): void {
    this.generation += 1;
    if (this.speaking) {
      this.speaking.stop();
      this.speaking = null;
    }
    if (this.el) {
      this.selfStopping = true;
      try {
        this.el.pause();
        this.el.currentTime = 0;
      } catch {
        /* jsdom / a refusing platform */
      }
      this.selfStopping = false;
    }
    if (this.revokeCurrent) {
      this.revokeCurrent();
      this.revokeCurrent = null;
    }
    if (this.state.playing) this.set({ playing: false });
  }

  /**
   * Play one cue down the ladder. Resolves with the rung reached.
   *
   * Refusals that are NOT a rung: muted (nothing plays, `MUTED` reported), locked (no gesture yet — nothing is
   * attempted and nothing is claimed), recording (the evidence interlock).
   */
  async play(cue: GuidanceCue): Promise<GuidanceSource> {
    this.sequenceToken = {};
    return this.playOne(cue);
  }

  private async playOne(cue: GuidanceCue): Promise<GuidanceSource> {
    this.stopMedia();
    if (this.state.muted) {
      this.set({ source: 'MUTED', cueKey: cue.key, voiceName: null, ducked: false });
      return 'MUTED';
    }
    if (this.state.recording) {
      // Deliberately does NOT set a source: nothing was attempted, so nothing is reported as reached.
      return 'NONE';
    }
    if (!this.state.unlocked) return 'NONE';
    const gen = this.generation;
    this.set({ cueKey: cue.key, ducked: false });

    // ── rung 1: the reviewed, hashed server asset ────────────────────────────────────────────────
    if (cue.asset) {
      const load = this.deps.loadAsset ?? ((ref: GuidanceAudioRef) => fetchGuidanceAsset(ref));
      let result: AssetResult;
      try {
        result = await load(cue.asset, cue.locale);
      } catch {
        result = { ok: false, reason: 'UNREACHABLE' };
      }
      if (gen !== this.generation) {
        if (result.ok) result.revoke();
        return 'NONE';
      }
      if (result.ok) {
        const el = this.el;
        if (el) {
          this.revokeCurrent = result.revoke;
          try {
            el.src = result.objectUrl;
            const p = el.play?.();
            if (p && typeof p.catch === 'function') p.catch(() => undefined);
          } catch {
            /* a refused play still counts as the asset rung reached — the element holds it */
          }
          this.set({ playing: true, source: 'SERVER_ASSET', voiceName: null });
          return 'SERVER_ASSET';
        }
        result.revoke();
      }
    }

    // ── rung 2: the device voice — for step cues AND (US-03482 G7) a notice with no reviewed asset; never for words
    //    in another language than the chosen one: silence + the rung-3 note beats a wrong-language read ──
    if (textMatchesVoice(cue)) {
      const tag = bcp47For(cue.locale);
      const doSpeak =
        this.deps.speakText ?? ((text: string, t: string, onEnd: (error?: string) => void) => speak(text, t, { onEnd }));
      const handle = doSpeak(cue.text, tag, (error?: string) => {
        if (gen !== this.generation) return;
        // The read is over: drop the handle so the next section's stopMedia() has nothing live to cancel (iOS
        // swallows an utterance spoken right after cancel()).
        this.speaking = null;
        if (error) {
          // Our own cancel never reports here (the handle is stopped first). Any platform error ends the chain;
          // `not-allowed` (no gesture / blocked) is rung 3 — the honest note, not a silent "playing".
          this.sequenceToken = {};
          if (error === 'not-allowed') this.set({ playing: false, source: 'NONE', voiceName: null });
          else if (this.state.playing) this.set({ playing: false });
          return;
        }
        if (this.state.playing) this.set({ playing: false });
      });
      if (gen !== this.generation) {
        handle?.stop();
        return 'NONE';
      }
      if (handle) {
        this.speaking = handle;
        this.set({ playing: true, source: 'DEVICE_TTS', voiceName: handle.voiceName });
        return 'DEVICE_TTS';
      }
    }

    // ── rung 3: silence WITH a visible note (the surface reads `source === 'NONE'`) ───────────────
    this.set({ playing: false, source: 'NONE', voiceName: null });
    return 'NONE';
  }

  /**
   * Read a notice section by section (§3.2). Each section is its own cue so a long notice can be stopped at any
   * point; the sequence advances on the section ending, and any cancel (a tap elsewhere, the mute toggle) ends it.
   * NEVER gates or advances the consent action — the "I agree and start" tap is the only consent (DPDP).
   */
  async playSections(sections: readonly GuidanceCue[]): Promise<GuidanceSource> {
    if (sections.length === 0) return 'NONE';
    const token: object = {};
    this.sequenceToken = token;
    const reached = await this.playOne(sections[0] as GuidanceCue);
    if (reached !== 'SERVER_ASSET' && reached !== 'DEVICE_TTS') return reached;
    // The rest chain off each section ENDING (the element's `ended`, the utterance's end). A cancel — the mute
    // toggle, a step change, "I agree and start" — replaces the token and the read simply stops.
    void (async () => {
      for (const section of sections.slice(1)) {
        const finished = await this.whenIdle(token);
        if (!finished || this.sequenceToken !== token) return;
        const next = await this.playOne(section);
        if (next !== 'SERVER_ASSET' && next !== 'DEVICE_TTS') return;
      }
    })();
    return reached;
  }

  /** Resolves true when the current cue finished on its own, false when something took the sequence away. */
  private whenIdle(token: object): Promise<boolean> {
    return new Promise((resolve) => {
      const tick = (): void => {
        if (this.sequenceToken !== token) {
          resolve(false);
          return;
        }
        if (!this.state.playing) {
          resolve(true);
          return;
        }
        const unsub = this.subscribe(() => {
          unsub();
          tick();
        });
      };
      tick();
    });
  }

  /** Test/teardown hook: drop the element and every handle. */
  dispose(): void {
    this.cancel();
    this.el = null;
    this.listeners.clear();
    this.state = { ...this.state, unlocked: false, playing: false, source: null, cueKey: null, voiceName: null, ducked: false };
  }
}
